import { Component, forwardRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';

const STEP_MINUTES = ['00', '15', '30', '45'];
const HOURS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, '0'));

// Orario a scatti di 15 minuti: il campo nativo ignora "step" su iOS, quindi
// due menu (ora + minuti) che sul telefono aprono la rotella nativa.
// Valore "HH:mm" oppure "" se non impostato.
@Component({
  selector: 'app-time-picker',
  imports: [CommonModule, TranslatePipe],
  template: `
    <span class="tp" [class.empty]="!hour">
      <select
        class="tp-select"
        [disabled]="disabled"
        (change)="onHour($any($event.target).value)"
        [attr.id]="inputId"
        [attr.aria-label]="'time_picker.hour' | translate"
      >
        <option value="" [selected]="!hour">--</option>
        <option *ngFor="let h of hours" [value]="h" [selected]="h === hour">{{ h }}</option>
      </select>
      <span class="tp-sep">:</span>
      <select
        class="tp-select"
        [disabled]="disabled || !hour"
        (change)="onMinute($any($event.target).value)"
        [attr.aria-label]="'time_picker.minutes' | translate"
      >
        <option *ngFor="let m of minutes" [value]="m" [selected]="m === minute">{{ m }}</option>
      </select>
    </span>
  `,
  styles: [
    `
      :host { display: inline-flex; }
      .tp { display: inline-flex; align-items: center; gap: 0.15rem; }
      .tp-select {
        min-width: 3.4rem;
        min-height: var(--tp-height, 40px);
        padding: 0.3rem 0.5rem;
        border: 1px solid var(--border);
        border-radius: var(--tp-radius, 10px);
        background: var(--tp-bg, var(--cream));
        color: var(--text-dark);
        font-family: inherit;
        font-size: 0.95rem;
        text-align: center;
        text-align-last: center;
        appearance: none;
        -webkit-appearance: none;
        cursor: pointer;
      }
      .tp-select:focus { outline: none; border-color: var(--gold); }
      .tp-select:disabled { opacity: 0.5; cursor: default; }
      .tp-sep { font-weight: 700; color: var(--text-mid); }
    `,
  ],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimePickerComponent), multi: true },
  ],
})
export class TimePickerComponent implements ControlValueAccessor {
  @Input() inputId: string | null = null;

  readonly hours = HOURS;
  hour = '';
  minute = '00';
  disabled = false;

  private onChange: (v: string) => void = () => {};
  private onTouched: () => void = () => {};

  // Un orario già salvato fuori dagli scatti (es. 13:53) resta selezionabile
  get minutes(): string[] {
    return STEP_MINUTES.includes(this.minute) ? STEP_MINUTES : [...STEP_MINUTES, this.minute].sort();
  }

  writeValue(value: string | null): void {
    const match = /^(\d{1,2}):(\d{2})/.exec(value ?? '');
    if (match) {
      this.hour = match[1].padStart(2, '0');
      this.minute = match[2];
    } else {
      this.hour = '';
      this.minute = '00';
    }
  }

  registerOnChange(fn: (v: string) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onHour(value: string): void {
    this.hour = value;
    this.emit();
  }

  onMinute(value: string): void {
    this.minute = value;
    this.emit();
  }

  private emit(): void {
    this.onChange(this.hour ? `${this.hour}:${this.minute}` : '');
    this.onTouched();
  }
}
